import { useCallback, useRef, useState } from "react";
import { LayoutGrid } from "lucide-react";
import type { LatestReading, SiteInfo } from "@spf/contracts";
import type { StreamStatus } from "@spf/api-client";
import { useLiveTelemetry } from "../lib/useLiveTelemetry.js";
import { useFreshness, type FreshnessVerdict } from "../lib/useFreshness.js";
import { usePreferences, type WidgetId } from "../lib/preferences.js";
import { useAsync } from "../lib/useAsync.js";
import { useEnergy, type EnergyResult } from "../lib/useEnergy.js";
import { CHART_QUERIES, TODAY_QUERY, chartState } from "../lib/energy.js";
import { api } from "../lib/client.js";
import { Card } from "../components/Card.js";
import { Freshness } from "../components/Freshness.js";
import { HeroCard } from "../components/widgets/HeroCard.js";
import { EstimatedFullWidget } from "../components/widgets/EstimatedFullWidget.js";
import { BatteryStatusWidget } from "../components/widgets/BatteryStatusWidget.js";
import { TotalEnergyWidget } from "../components/widgets/TotalEnergyWidget.js";
import { LoadBalanceWidget } from "../components/widgets/LoadBalanceWidget.js";
import { MainChartWidget, type ChartRange } from "../components/widgets/MainChartWidget.js";
import { EarningWidget } from "../components/widgets/EarningWidget.js";
import { WeatherWidget } from "../components/widgets/WeatherWidget.js";
import { EnergyFlowWidget } from "../components/widgets/EnergyFlowWidget.js";
import { MobileSwipeStatsDeck, type DeckCard } from "../components/widgets/MobileSwipeStatsDeck.js";
import { WidgetDetailModal } from "../components/modals/WidgetDetailModal.js";
import { Empty, Failed, Loading, buttonClass } from "../components/ViewState.js";
import { useAlerts } from "../lib/alerts.js";
import { formatAge, formatDateTime } from "../lib/format.js";

/**
 * The dashboard, composed as the design composes it: a four-column bento of
 * nine widgets, in the reader's own order, off the same held-open connection
 * the Live view uses.
 *
 * One of the nine has no data behind it on this installation and is here
 * anyway, showing its empty state -- the layout is what is being ported, and
 * a widget that appears later shifts everything below it. `WeatherWidget`
 * waits on a source this deployment does not have.
 *
 * The history chart, the energy summary and the grid cost read derived
 * energy, fetched again with each new reading so none freezes while the page
 * stays open.
 */
export function DashboardPage() {
  const { status, latest, retry } = useLiveTelemetry();
  const heading = useRef<HTMLHeadingElement>(null);
  const { widgets } = usePreferences();
  const { state: site } = useAsync(api.site, "Could not load site details.");
  const [range, setRange] = useState<ChartRange>("6 months");
  const [detail, setDetail] = useState<WidgetId | null>(null);
  const alerts = useAlerts();

  const retryHere = useCallback(() => {
    retry();
    heading.current?.focus();
  }, [retry]);
  const freshness = useFreshness(latest);
  const newest = latest?.reading?.time ?? null;
  const energy = {
    chart: useEnergy(CHART_QUERIES[range], newest),
    today: useEnergy(TODAY_QUERY, newest),
  };

  return (
    <section aria-labelledby="dashboard-heading" className="flex flex-col gap-5">
      {/* The banner is the design's, and the freshness line sits under it on
          its own row rather than beside the title. Alongside, the stale
          sentence is a paragraph competing with a heading for the same row and
          it wins -- at 1440 it took two thirds of the width and pushed the
          title into a corner. */}
      <div>
        <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">
          <LayoutGrid size={14} aria-hidden="true" className="text-accent-ink" />
          Everything at once
        </p>
        <h2
          id="dashboard-heading"
          ref={heading}
          tabIndex={-1}
          className="text-3xl font-bold tracking-tight text-ink outline-none sm:text-4xl"
        >
          Dashboard
        </h2>
      </div>

      {latest !== null && (
        <Connection status={status} value={latest} freshness={freshness} onRetry={retryHere} />
      )}

      {latest === null ? (
        <Card title="Connection">
          <Connecting status={status} onRetry={retryHere} />
        </Card>
      ) : latest.reading === null ? (
        <Card title="Connection">
          <Empty>
            No telemetry yet. Once the inverter&rsquo;s dongle reports, this page updates on its own
            within about five minutes.
          </Empty>
        </Card>
      ) : (
        <>
          {/*
            Two arrangements of the same nine widgets, as the design has them.
            Below the tablet breakpoint the bento is a single column of nine
            tall cards, which is a long way to scroll to reach the last, so
            there the deck shows one at a time. Both are rendered and one is
            hidden, which is how a CSS breakpoint switches layout without a
            resize listener deciding it in JavaScript.
          */}
          <div className="md:hidden">
            <MobileSwipeStatsDeck
              cards={
                widgets.map((id) => ({
                  id,
                  name: WIDGET_NAMES[id],
                  category: WIDGET_CATEGORIES[id],
                  node: (
                    <Panel
                      id={id}
                      value={latest}
                      freshness={freshness}
                      site={site.kind === "ready" ? site.value : null}
                      range={range}
                      onRangeChange={setRange}
                      energy={energy}
                      onExpand={setDetail}
                      alerts={alerts.length}
                    />
                  ),
                })) satisfies DeckCard[]
              }
            />
          </div>

          <div className="hidden auto-rows-min grid-cols-1 gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
            {widgets.map((id) => (
              <Panel
                key={id}
                id={id}
                value={latest}
                freshness={freshness}
                site={site.kind === "ready" ? site.value : null}
                range={range}
                onRangeChange={setRange}
                energy={energy}
                onExpand={setDetail}
                alerts={alerts.length}
              />
            ))}
          </div>
        </>
      )}

      {detail !== null && (
        <WidgetDetailModal
          id={detail}
          value={
            latest ?? {
              reading: null,
              battery: null,
              ageSeconds: null,
              stale: false,
              staleAfterSeconds: 0,
            }
          }
          site={site.kind === "ready" ? site.value : null}
          ageSeconds={freshness.ageSeconds}
          onClose={() => {
            setDetail(null);
          }}
        />
      )}
    </section>
  );
}

/** The names the deck's chips and the detail sheet use. */
const WIDGET_NAMES: Record<WidgetId, string> = {
  welcome: "Site",
  estFull: "Estimated full",
  battery: "Battery",
  totalEnergy: "Energy summary",
  loadBalance: "Load balance",
  mainChart: "History",
  earning: "Grid cost",
  weather: "Weather",
  energyFlow: "Energy flow",
};

/** The design's small caption above each deck card. */
const WIDGET_CATEGORIES: Record<WidgetId, string> = {
  welcome: "System",
  estFull: "Charge",
  battery: "Reserve",
  totalEnergy: "Delivered",
  loadBalance: "Load",
  mainChart: "History",
  earning: "Cost",
  weather: "Climate",
  energyFlow: "Flow",
};

/**
 * One widget, chosen by id.
 *
 * A lookup rather than nine sibling `&&` blocks, which is what the design has:
 * the order is the reader's, so the list is driven by their array rather than
 * by source order.
 */
function Panel({
  id,
  value,
  freshness,
  site,
  range,
  onRangeChange,
  energy,
  onExpand,
  alerts,
}: {
  id: WidgetId;
  value: LatestReading;
  freshness: FreshnessVerdict;
  site: SiteInfo | null;
  range: ChartRange;
  onRangeChange: (range: ChartRange) => void;
  energy: { chart: EnergyResult; today: EnergyResult };
  onExpand: (id: WidgetId) => void;
  alerts: number;
}) {
  const reading = value.reading;
  if (reading === null) return null;
  const expand = () => {
    onExpand(id);
  };

  switch (id) {
    case "welcome":
      return (
        <HeroCard
          siteName={site?.siteName ?? "there"}
          mode={directionOf(reading.batteryPowerW)}
          alerts={alerts}
          onShowAlerts={expand}
        />
      );
    case "estFull":
      return <EstimatedFullWidget outlook={value.battery} onExpand={expand} />;
    case "battery":
      return <BatteryStatusWidget reading={reading} outlook={value.battery} onExpand={expand} />;
    case "totalEnergy":
      return <TotalEnergyWidget today={energy.today} onExpand={expand} />;
    case "loadBalance":
      return <LoadBalanceWidget reading={reading} onExpand={expand} />;
    case "mainChart":
      return (
        <MainChartWidget
          state={chartState(energy.chart.state)}
          onRetry={energy.chart.retry}
          range={range}
          onRangeChange={onRangeChange}
          onExpand={expand}
        />
      );
    case "earning":
      return (
        <EarningWidget today={energy.today} currency={site?.currency ?? "NGN"} onExpand={expand} />
      );
    case "weather":
      return <WeatherWidget onExpand={expand} />;
    case "energyFlow":
      return <EnergyFlowWidget reading={reading} stale={freshness.stale} onExpand={expand} />;
    default:
      return null;
  }
}

function directionOf(batteryPowerW: number): string {
  if (batteryPowerW < 0) return "Charging";
  if (batteryPowerW > 0) return "Discharging";
  return "Idle";
}

function Connecting({ status, onRetry }: { status: StreamStatus; onRetry: () => void }) {
  if (status === "crowded") {
    return <Failed message={`${CROWDED} Then try again.`} onRetry={onRetry} />;
  }
  if (status === "offline") {
    return (
      <Failed
        message="Could not connect for live updates. The server may be unreachable."
        onRetry={onRetry}
      />
    );
  }
  return <Loading label="Connecting for live updates…" />;
}

const OFFLINE = "Not receiving live updates, and trying to reconnect.";
const CROWDED =
  "Live updates have stopped: this account already has as many of them open as it allows. " +
  "Close another tab or device showing this page.";

/** The same rule as the Live view: `connecting` is not alarmed on. */
function Connection({
  status,
  value,
  freshness,
  onRetry,
}: {
  status: StreamStatus;
  value: LatestReading;
  freshness: FreshnessVerdict;
  onRetry: () => void;
}) {
  if (status === "live" || status === "connecting") {
    return <Freshness value={value} freshness={freshness} />;
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <p
        className="rounded-2xl border border-danger/30 bg-danger/10 px-4 py-2 text-sm text-danger-ink"
        role="status"
      >
        {status === "crowded" ? CROWDED : OFFLINE} {lastReading(value, freshness)}
      </p>
      <button type="button" onClick={onRetry} className={buttonClass}>
        Try again
      </button>
    </div>
  );
}

function lastReading(value: LatestReading, freshness: FreshnessVerdict): string {
  const at = value.reading?.time;
  if (at === undefined) return "Nothing has arrived yet.";
  if (freshness.ageSeconds === null) return `The last reading was at ${formatDateTime(at)}.`;
  return `The last reading was ${formatAge(freshness.ageSeconds)}, at ${formatDateTime(at)}.`;
}
